import React, { useEffect, useState } from 'react';
import { Modal, Spin, Button } from 'antd';
import { MergedOrder } from '../../../../types/process/order';
import { purchaseApi } from '../../../../services/process/purchase';
import { PurchaseEntity } from '../../../../types/process/purchase';
import { useStaffMap } from '../../../../services/store/organization.store';
import dayjs from 'dayjs';

export interface DecisionInfoModalProps {
  visible: boolean;
  order: MergedOrder | null;
  onCancel: () => void;
}

const formatDateTime = (v?: string | number) => {
  if (!v) return '-';
  const d = dayjs(v);
  if (!d.isValid()) return String(v);
  return d.format('YYYY-MM-DD HH:mm:ss');
};

const ACTIVATION_STATUS_MAP: Record<number, string> = {
  1: '激活',
  2: '未激活',
  9: '未知',
};

const LOCK_STATUS_MAP: Record<number, string> = {
  1: '上锁',
  2: '不上锁',
  9: '未知',
};

const DecisionInfoModal: React.FC<DecisionInfoModalProps> = ({
  visible,
  order,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [purchaseInfo, setPurchaseInfo] = useState<PurchaseEntity | null>(null);
  const { staffMap } = useStaffMap();

  useEffect(() => {
    if (visible && order) {
      const deliveryStatus = order.order.deliveryStatus || 0;
      const isSelfDelivery = deliveryStatus >= 11 && deliveryStatus <= 13;

      if (isSelfDelivery) {
        setLoading(true);
        // Fetch the latest purchase order for this order
        purchaseApi.list({
          page: 1,
          pageSize: 1,
          filter: { order_no: order.order.orderNo },
          sort: []
        }).then(res => {
          if (res.data?.items?.length > 0) {
            setPurchaseInfo(res.data.items[0].purchase);
          } else {
            setPurchaseInfo(null);
          }
        }).catch(() => {
          setPurchaseInfo(null);
        }).finally(() => {
          setLoading(false);
        });
      } else {
        setPurchaseInfo(null);
      }
    } else {
      setPurchaseInfo(null);
    }
  }, [visible, order]);

  if (!order) return null;

  const decisionStaff = staffMap.get(order.order.decisionOperatorId || 0);
  const decisionName = decisionStaff?.name || (order.order.decisionOperatorId ? String(order.order.decisionOperatorId) : '-');
  
  const deliveryStatus = order.order.deliveryStatus || 0;
  const isPlatformDelivery = deliveryStatus >= 1 && deliveryStatus <= 3;
  const isSelfDelivery = deliveryStatus >= 11 && deliveryStatus <= 13;
  const deliveryTypeName = isPlatformDelivery ? '平台发货' : (isSelfDelivery ? '自采' : '-');

  const activationText = purchaseInfo?.activationStatus ? ACTIVATION_STATUS_MAP[purchaseInfo.activationStatus] || '未知' : '-';
  const lockText = purchaseInfo?.lockStatus ? LOCK_STATUS_MAP[purchaseInfo.lockStatus] || '未知' : '-';
  const pickupText = purchaseInfo?.pickupMethod || '-';

  return (
    <Modal
      title="决策信息详情"
      open={visible}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" onClick={onCancel}>我知道了</Button>
        </div>
      }
      width={500}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 12 }}>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, color: '#6a7282' }}>决策人</span>
            <span style={{ fontSize: 14, color: '#101828', fontWeight: 500 }}>{decisionName}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, color: '#6a7282' }}>决策时间</span>
            <span style={{ fontSize: 14, color: '#101828', fontWeight: 500 }}>{formatDateTime(order.order.decisionTime)}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, color: '#6a7282' }}>采购渠道</span>
            <div style={{ backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: '12px', fontSize: 14, color: '#1e2939' }}>
              {deliveryTypeName}
            </div>
          </div>

          {isSelfDelivery && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, color: '#6a7282' }}>采购需求</span>
                <div style={{ backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: '12px', fontSize: 14, color: '#1e2939' }}>
                  {`激活需求: ${activationText} | 上锁需求: ${lockText} | 揽收需求: ${pickupText}`}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, color: '#6a7282' }}>其他需求说明</span>
                <div style={{ backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: '12px', fontSize: 14, color: '#1e2939', minHeight: 46 }}>
                  {purchaseInfo?.requirementRemark || '-'}
                </div>
              </div>
            </>
          )}

        </div>
      </Spin>
    </Modal>
  );
};

export default DecisionInfoModal;
